import { fetchWrapper } from './previewMockCalls';
import envVars from 'env-vars';

export interface UploadResult {
  url: string;
  originalFileUrl?: string;
}

const HEIC_TYPES = ['image/heic', 'image/heif'];

function isHeicFile(file: File): boolean {
  return (
    HEIC_TYPES.includes(file.type) ||
    /\.heic$/i.test(file.name) ||
    /\.heif$/i.test(file.name)
  );
}

export const uploadToS3 = async (file: File): Promise<UploadResult> => {
  try {
    if (isHeicFile(file)) {
      const [convertedUrl, originalUrl] = await Promise.all([
        uploadConvertedFile(file),
        uploadOriginalFile(file),
      ]);
      return {
        url: convertedUrl,
        originalFileUrl: originalUrl || undefined,
      };
    }

    const presigned = await getPresignedPost(file);
    const url = await uploadFile(file, presigned);
    return { url };
  } catch (error) {
    throw error;
  }
};

async function uploadConvertedFile(file: File): Promise<string> {
  const presigned = await getPresignedPost(file);
  return await uploadFile(file, presigned);
}

async function uploadOriginalFile(file: File): Promise<string> {
  const presigned = await getPresignedPostForOriginal(file);
  const formData = new FormData();
  const fileBuffer = await file.arrayBuffer();
  Object.entries(presigned.fields).forEach(([key, value]: [string, any]) => {
    formData.append(key, value);
  });
  formData.append('Content-Type', file.type || 'image/heic');
  formData.append(
    'file',
    new Blob([fileBuffer], { type: file.type || 'image/heic' })
  );

  try {
    const res = await fetch(presigned.url, {
      method: 'POST',
      body: formData,
    });
    const location: string = res.headers.get('Location') || '';
    return decodeURIComponent(location);
  } catch (error) {
    return '';
  }
}

async function getPresignedPostForOriginal(file: File) {
  const API_URL =
    envVars.REACT_APP_STAND_ALONE_MODE === 'false'
      ? envVars.REACT_APP_API_URL
      : '';

  try {
    const response = await fetchWrapper(`${API_URL}/app_api/upload`, {
      method: 'POST',
      headers: new Headers({ 'content-type': 'application/json' }),
      body: JSON.stringify({
        filename: file.name.toLowerCase(),
      }),
    });

    return await response.json();
  } catch (error) {
    throw error;
  }
}

export async function getPresignedPost(file: File) {
  const API_URL =
    envVars.REACT_APP_STAND_ALONE_MODE === 'false'
      ? envVars.REACT_APP_API_URL
      : '';

  try {
    const response = await fetchWrapper(`${API_URL}/app_api/upload`, {
      method: 'POST',
      headers: new Headers({ 'content-type': 'application/json' }),
      body: JSON.stringify({
        filename: file.name.toLowerCase().replace(/\.heic/i, '.jpeg'),
      }),
    });

    return await response.json();
  } catch (error) {
    throw error;
  }
}

export async function uploadFile(file: File, presignedPost: any) {
  const formData = new FormData();
  const fileBuffer = await file.arrayBuffer();
  Object.entries(presignedPost.fields).forEach(
    ([key, value]: [string, any]) => {
      formData.append(key, value);
    }
  );
  if (file.type === 'image/heic') {
    // @ts-ignore
    const heicConvert = (await import('heic-convert/browser')).default;
    const buffer = new Uint8Array(await file.arrayBuffer());
    const convertedBuffer = await heicConvert({
      buffer: buffer,
      format: 'JPEG',
      quality: 0.8,
    });
    formData.append('Content-Type', 'image/jpeg');
    formData.append(
      'file',
      new Blob([convertedBuffer], { type: 'image/jpeg' })
    );
  } else {
    formData.append('Content-Type', file.type);
    formData.append('file', new Blob([fileBuffer], { type: file.type }));
  }

  try {
    const res = await fetch(presignedPost.url, {
      method: 'POST',
      body: formData,
    });

    const location: string = res.headers.get('Location') || '';

    return decodeURIComponent(location);
  } catch (error) {
    return '';
  }
}
